/**
 * Runtime-resolvable backend URL.
 *
 * Order of precedence:
 *   1. localStorage['ghostcomm_server_url']  — user-configured (e.g., on APK first launch)
 *   2. process.env.REACT_APP_BACKEND_URL    — baked at build time (default hosted backend)
 *
 * Why: the packaged Android APK needs to let self-hosting users point it at their own server.
 */

import { setItem as lsSet, removeItem as lsRemove } from './storage';

const LS_KEY = 'ghostcomm_server_url';

export function getServerUrl() {
  try {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null;
    if (v && /^https?:\/\//i.test(v)) return v.replace(/\/+$/, '');
  } catch {}
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
}

export function setServerUrl(url) {
  const trimmed = (url || '').trim().replace(/\/+$/, '');
  if (!trimmed) {
    lsRemove(LS_KEY);
    return;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    throw new Error('Server URL must start with http:// or https://');
  }
  lsSet(LS_KEY, trimmed);
}

export function clearServerUrl() {
  lsRemove(LS_KEY);
}

/** Health-check a candidate server URL. Returns true if /api/desktop/info responds OK. */
export async function probeServer(url) {
  const clean = (url || '').trim().replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(clean)) return { ok: false, error: 'Invalid URL' };
  try {
    const res = await fetch(`${clean}/api/desktop/info`, {
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout ? AbortSignal.timeout(6000) : undefined,
    });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { ok: true, version: data?.version || 'unknown' };
  } catch (e) {
    // Map common network errors to user-friendly strings instead of leaking DOMException text.
    const name = e?.name || '';
    if (name === 'AbortError' || name === 'TimeoutError') {
      return { ok: false, error: 'Timed out' };
    }
    if (name === 'TypeError') {
      // Browsers throw TypeError for DNS fail / CORS block / mixed-content / network down.
      return { ok: false, error: 'Server unreachable' };
    }
    if (name === 'SyntaxError') {
      return { ok: false, error: 'Response was not valid JSON' };
    }
    return { ok: false, error: e?.message || 'Network error' };
  }
}
